import { useCallback, useMemo } from 'react';

import { useGetSupports } from '@/features/programs/api/get-supports';
import { usePostSupport } from '@/features/programs/api/post-support';
import {
  buildSupportOptions,
  getSupportAlias,
  getSupportValue,
  setSupportLabelCache,
} from '@/features/programs/helpers/supports';

export function useSupportOptions() {
  const { data: allSupports = [], refetch: refetchSupports } = useGetSupports();
  const { mutateAsync: createSupport, isPending: isCreatingSupport } = usePostSupport();

  const supportOptions = useMemo(() => buildSupportOptions(allSupports), [allSupports]);

  const createSupportOption = useCallback(
    async (name: string) => {
      const previousIds = new Set(allSupports.map(getSupportValue).filter(Boolean));
      const support = await createSupport({ name });
      const refreshedSupports = (await refetchSupports()).data || [];
      const normalizedName = name.trim().toLocaleLowerCase();

      const matchesName = (itemName: string | undefined) =>
        itemName?.trim().toLocaleLowerCase() === normalizedName;

      const createdFromList =
        refreshedSupports.find(item => {
          const value = getSupportValue(item);
          return (
            Boolean(value) &&
            !previousIds.has(value) &&
            (matchesName(getSupportAlias(item)) || matchesName(item.name))
          );
        }) ||
        refreshedSupports.find(
          item => matchesName(getSupportAlias(item)) || matchesName(item.name)
        );

      const value = getSupportValue(createdFromList || support);
      const label = getSupportAlias(createdFromList || support) || name;

      if (value && label) {
        setSupportLabelCache(value, label);
      }

      return {
        support: createdFromList || support,
        value,
        label,
      };
    },
    [allSupports, createSupport, refetchSupports]
  );

  return {
    allSupports,
    supportOptions,
    isCreatingSupport,
    createSupportOption,
  };
}
